import React, { useState } from "react";
import { Node, mergeAttributes } from "@tiptap/core";
import { ReactNodeViewRenderer, NodeViewWrapper } from "@tiptap/react";

/**
 * imagePlaceholder — a captioned "add a photo here" card that YETI drops into
 * sections where an image would help. Admins click it to upload; it then
 * replaces itself with the real image. Players just see nothing (it renders as
 * a subtle hint that admins can fill; in read-only it shows the caption only).
 *
 * The fill handler is injected via `configure({ onFill })` — onFill() opens the
 * picker, uploads, and resolves the image URL.
 */
const PlaceholderCard = ({ node, editor, getPos, extension }) => {
  const [busy, setBusy] = useState(false);
  const caption = node.attrs.caption || "Add a photo here";
  const editable = editor.isEditable;

  // Players never see an empty placeholder — only admins filling it.
  if (!editable) return <NodeViewWrapper className="pb-imgph-hidden" />;

  const fill = async () => {
    const { onFill } = extension.options;
    if (!onFill || busy) return;
    setBusy(true);
    try {
      const src = await onFill();
      if (src && typeof getPos === "function") {
        const pos = getPos();
        editor
          .chain()
          .focus()
          .insertContentAt({ from: pos, to: pos + node.nodeSize }, { type: "image", attrs: { src } })
          .run();
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <NodeViewWrapper className="pb-imgph" contentEditable={false}>
      <button
        type="button"
        className="pb-imgph-btn"
        onClick={editable ? fill : undefined}
        disabled={!editable || busy}
      >
        <span className="pb-imgph-ic">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <circle cx="8.5" cy="8.5" r="1.5" />
            <path d="m21 15-5-5L5 21" />
          </svg>
        </span>
        <span className="pb-imgph-text">
          <span className="pb-imgph-t">{busy ? "Uploading…" : "Add a photo"}</span>
          <span className="pb-imgph-c">{caption}</span>
        </span>
        {editable && !busy && <span className="pb-imgph-cta">Click to upload</span>}
      </button>
    </NodeViewWrapper>
  );
};

export const ImagePlaceholder = Node.create({
  name: "imagePlaceholder",
  group: "block",
  atom: true,
  selectable: true,
  draggable: false,

  addOptions() {
    return { onFill: null };
  },

  addAttributes() {
    return {
      caption: {
        default: "",
        parseHTML: (el) => el.getAttribute("caption") || el.getAttribute("data-caption") || "",
        renderHTML: (attrs) => (attrs.caption ? { caption: attrs.caption } : {}),
      },
    };
  },

  parseHTML() {
    return [{ tag: "figure[data-image-placeholder]" }];
  },

  renderHTML({ HTMLAttributes }) {
    return ["figure", mergeAttributes(HTMLAttributes, { "data-image-placeholder": "true" })];
  },

  addNodeView() {
    return ReactNodeViewRenderer(PlaceholderCard);
  },
});

export default ImagePlaceholder;
